import React, { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';

type PopupPosition = React.ComponentProps<typeof Popup>['position'];
type PopupSize = React.ComponentProps<typeof Popup>['size'];
type PopupOn = React.ComponentProps<typeof Popup>['on'];

interface TruncatedTextProps {
    /** The text to render. Always single-line, clipped with an ellipsis when it does not fit. */
    children: string;
    className?: string;
    /** Popup body. Defaults to the text itself. */
    popupContent?: React.ReactNode;
    /** Show the popup even when the text fits (used when the popup carries extra detail). */
    forcePopup?: boolean;
    position?: PopupPosition;
    size?: PopupSize;
    as?: 'div' | 'span' | 'p';
    /** Defaults to hover-only; pass `['hover', 'click']` where the popup carries
     *  information (e.g. a full address) that touch users need a tap to reach. */
    on?: PopupOn;
    /** Keep the requested `position` instead of letting Popper flip it. */
    pinned?: boolean;
    /** Passed through to Semantic's Popup. Use to disable `preventOverflow` when
     *  an `overflow: hidden` ancestor would otherwise squeeze the popup. */
    popperModifiers?: React.ComponentProps<typeof Popup>['popperModifiers'];
}

/**
 * Single-line text that ellipsises instead of overflowing its parent, and surfaces
 * the full string in a Semantic popup whenever it is actually clipped.
 *
 * The trigger is an `inline-block` inside a plain block wrapper, NOT a block
 * itself. Semantic positions a popup against the trigger's box, so a block
 * trigger (full container width) makes `position="bottom center"` point at the
 * middle of the container instead of at the text — visibly wrong as soon as the
 * container is wider than the string. `inline-block` + `max-w-full` keeps the box
 * hugging the text while still clipping at the container's edge.
 *
 * The wrapper also keeps successive TruncatedTexts on their own lines, which
 * bare inline-blocks would not do.
 */
const TruncatedText = ({
    children,
    className = '',
    popupContent,
    forcePopup = false,
    position = 'bottom center',
    size = 'small',
    as = 'div',
    on = 'hover',
    pinned,
    popperModifiers,
}: TruncatedTextProps) => {
    const nodeRef = useRef<HTMLSpanElement | null>(null);
    const [isClipped, setIsClipped] = useState(false);

    const measure = useCallback(() => {
        const node = nodeRef.current;
        if (!node) return;
        // +1 guards against sub-pixel rounding reporting a false positive.
        setIsClipped(node.scrollWidth > node.offsetWidth + 1);
    }, []);

    useEffect(() => {
        measure();

        const node = nodeRef.current;
        const ResizeObserverCtor = (window as any).ResizeObserver;
        if (node && ResizeObserverCtor) {
            const observer = new ResizeObserverCtor(() => measure());
            observer.observe(node);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure, children]);

    const text = (
        <span ref={nodeRef} className={`inline-block max-w-full truncate align-top ${className}`.trim()}>
            {children}
        </span>
    );

    // Always mounted through `Popup` (toggled via `disabled`, not swapped in/out of
    // the tree): switching the trigger between a bare span and a Popup-wrapped one
    // changes its element type at this position, so React remounts it — and the
    // ResizeObserver above, already bound to the old DOM node, would keep watching
    // a detached element instead of the new one.
    return createElement(
        as,
        { className: 'block min-w-0' },
        <Popup
            disabled={!(isClipped || forcePopup)}
            content={popupContent || children}
            trigger={text}
            on={on}
            size={size}
            position={position}
            pinned={pinned}
            popperModifiers={popperModifiers}
        />,
    );
};

export default TruncatedText;
